import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { AgentIconWithFallback } from 'ai-components-ui-library/components/icons/AgentIconWithFallback';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { FavoriteIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardSection } from 'foundations-components/transitional/components/Card';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import { useMemo } from 'react';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { DetailsPageCard } from './DetailsPageCard';
import { DefaultButton } from '../../install/DefaultButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ProductBuyBox({
  companyName,
  companyUrl,
  formUrl,
  installsCount,
  isAI = false,
  meetingUrl,
  reviewSummary,
  tagline,
  onTrackInteraction = () => {},
  primaryAction
}) {
  const {
    iconUrl,
    productType,
    name
  } = useDetailsPageContext();
  const scrollToReviews = () => {
    const tab = document.querySelector(`#details-page-reviews`);
    if (tab) {
      tab.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const renderedPricingMessage = useMemo(() => {
    if (productType === 'APP') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.pricingMessage.subscription",
        options: {
          name
        }
      });
    }
    // TODO: reinstate when pricing is finalized
    // if (productType === 'AGENT') {
    //   return (
    //     <FormattedMessage message="detailsPage.productDetailsCard.pricingMessage.credits" />
    //   );
    // }
    return null;
  }, [productType, name]);
  const renderedScheduleDemoButton = useMemo(() => {
    return meetingUrl && formUrl ? /*#__PURE__*/_jsx(Dropdown, {
      buttonText: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.scheduleDemoButton"
        })
      }),
      buttonUse: "secondary",
      buttonSize: "small",
      marginInline: "0",
      children: /*#__PURE__*/_jsxs(List, {
        children: [/*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: meetingUrl,
            external: true,
            onClick: () => onTrackInteraction('click schedule meeting', {
              section: 'header'
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.scheduleMeetingButton"
            })
          })
        }), /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: formUrl,
            external: true,
            onClick: () => onTrackInteraction('click request to be contacted', {
              section: 'header'
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.requestToBeContactedButton"
            })
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(Button, {
      href: meetingUrl || formUrl,
      external: true,
      onClick: () => onTrackInteraction('click schedule demo', {
        section: 'header'
      }),
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: meetingUrl ? 'detailsPage.productDetailsCard.cta.scheduleDemoButton' : 'detailsPage.productDetailsCard.cta.requestToBeContactedButton'
      })
    });
  }, [meetingUrl, formUrl, onTrackInteraction]);
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: [isAI ?
      /*#__PURE__*/
      //TODO: remove this check when we get guaranteed agents iconUrl from unified details
      _jsx(AgentIconWithFallback, {
        iconUrl: iconUrl,
        name: name,
        size: 72
      }) : /*#__PURE__*/_jsx(Image, {
        blockSize: "72px",
        inlineSize: "72px",
        objectFit: "cover",
        src: iconUrl,
        alt: `${name}-icon`
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        flexDirection: "row",
        gap: "100",
        maxBlockSize: "200",
        marginBlockStart: "100",
        children: [/*#__PURE__*/_jsx(Badge, {
          use: "koala",
          justifyContent: "end",
          marginInline: "100",
          "data-test-id": "product-type-badge",
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            gap: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `detailsPage.productType.${productType}`
            })
          })
        }), isAI && /*#__PURE__*/_jsx(Flex, {
          "data-test-id": "ai-badge",
          children: /*#__PURE__*/_jsx(AIBadge, {})
        })]
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      paddingBlockStart: "200",
      paddingBlockEnd: "300",
      paddingInline: "0",
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h1",
        typography: "heading-300",
        textWrap: "wrap",
        truncate: {
          lineClamp: 2
        },
        children: name
      }), /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(TruncateMultiline, {
          text: tagline,
          maxChars: 70
        })
      }), /*#__PURE__*/_jsxs(DescriptionList, {
        display: "flex",
        flexDirection: "row",
        gap: "1000",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.builtBy"
            })
          }), /*#__PURE__*/_jsx("dd", {
            children: /*#__PURE__*/_jsx(Link, {
              href: companyUrl,
              external: true,
              children: companyName
            })
          })]
        }), installsCount &&
        /*#__PURE__*/
        //TODO: remove this check when we get agents installs count from unified details
        _jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.installs"
            })
          }), /*#__PURE__*/_jsx("dd", {
            children: formatInstalls(installsCount)
          })]
        }), reviewSummary && /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("dt", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.rating"
            })
          }), /*#__PURE__*/_jsx("dd", {
            children: /*#__PURE__*/_jsx(Link, {
              onClick: scrollToReviews,
              children: /*#__PURE__*/_jsxs(Text, {
                color: "text-core-default",
                children: [/*#__PURE__*/_jsx("span", {
                  children: reviewSummary.overallRating
                }), /*#__PURE__*/_jsx(IconHolder, {
                  icon: FavoriteIcon,
                  size: "200",
                  paddingBlock: "0",
                  paddingInline: "100"
                }), "(", /*#__PURE__*/_jsx(FormattedNumber, {
                  value: reviewSummary.reviewCount,
                  abbreviate: true
                }), ")"]
              })
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      flexGrow: 1,
      padding: "0",
      display: "flex",
      flexDirection: "column",
      gap: "200",
      children: [primaryAction !== null && primaryAction !== void 0 ? primaryAction : /*#__PURE__*/_jsx(DefaultButton, {}), /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-core-subtle",
        textAlign: "center",
        children: renderedPricingMessage
      }), (meetingUrl || formUrl) && renderedScheduleDemoButton]
    })]
  });
}